/* eslint-disable react/jsx-props-no-spreading */
import { IconProps } from '@radix-ui/react-icons/dist/types';
import React, { FC, ForwardRefExoticComponent, HTMLAttributes, ReactElement, RefAttributes } from 'react';
import { Cross1Icon } from '@radix-ui/react-icons';
import { cn } from 'src/domains/common/components/utils';

// Badge

type BadgeVariantType = 'default' | 'secondary' | 'outline' | 'destructive';
type BadgeIconType = 'close';

interface BadgeType extends HTMLAttributes<HTMLDivElement> {
    variant: BadgeVariantType;
    icon?: BadgeIconType;
    dataTest?: string;
}

interface BadgeValuesType {
    txtColor: string;
    bgColor: string;
    borderColor: string;
}

const getBadgeValues = (variant: BadgeVariantType): BadgeValuesType => {
    switch (variant) {
        case 'default':
            return {
                txtColor: 'text-white',
                bgColor: 'bg-primary',
                borderColor: 'border-primary',
            };
        case 'secondary':
            return {
                txtColor: 'text-black',
                bgColor: 'bg-secondary',
                borderColor: 'border-secondary',
            };
        case 'outline':
            return {
                txtColor: 'text-black',
                bgColor: 'bg-white',
                borderColor: 'border-secondary',
            };
        case 'destructive':
            return {
                txtColor: 'text-white',
                bgColor: 'bg-destructive',
                borderColor: 'border-destructive',
            };
    }
};

const getBadgeIcon = (
    icon: BadgeIconType | undefined
): ForwardRefExoticComponent<IconProps & RefAttributes<SVGSVGElement>> | null => {
    switch (icon) {
        case 'close':
            return Cross1Icon;
        case undefined:
            return null;
    }
};

export const Badge: FC<BadgeType> = ({ children, icon, variant, className, dataTest, ...props }): ReactElement => {
    const { txtColor, bgColor, borderColor } = getBadgeValues(variant);
    const Icon = getBadgeIcon(icon);
    return (
        <div
            className={cn(
                'rounded-md border border-solid px-2.5 py-0.5 h-5 flex items-center gap-1 cursor-pointer',
                txtColor,
                bgColor,
                borderColor,
                className
            )}
            data-test={dataTest}
            {...props}
        >
            <span className='text-xs text-capitalize'>{children}</span>
            {Icon !== null && <Icon className=' size-3' />}
        </div>
    );
};

// Badge Notification

interface BadgeNotificationType extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
    value: number;
    dataTest?: string;
}

export const BadgeNotification: FC<BadgeNotificationType> = ({
    value,
    className,
    dataTest,
    ...props
}): ReactElement => {
    return (
        <div
            className={cn('bg-destructive rounded-md text-white px-1 py-[1px] h-[18px] flex gap-1', className)}
            data-test={dataTest}
            {...props}
        >
            <span className='text-xs'>{value}</span>
        </div>
    );
};

// Badge Status

export type BadgeStatusVariantType = 'unsaved' | 'edit' | 'readonly';
export type BadgeValuesBonusType = 'active' | 'canceled' | 'expired' | 'claimed' | 'rolled-back';

interface BadgeStatusType extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
    variant: BadgeStatusVariantType;
    dataTest?: string;
}
interface BadgeBonusType extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
    variant: BadgeValuesBonusType;
    dataTest?: string;
}

interface BadgeStatusValuesType {
    label: string;
    bgColor: string;
    borderColor: string;
}

const getBadgeStatusValues = (variant: BadgeStatusVariantType): BadgeStatusValuesType => {
    switch (variant) {
        case 'unsaved':
            return {
                label: 'Unsaved',
                bgColor: 'bg-badge-unsaved',
                borderColor: 'border-badge-unsaved/40',
            };
        case 'edit':
            return {
                label: 'Edit',
                bgColor: 'bg-badge-edit',
                borderColor: 'border-badge-edit/40',
            };
        case 'readonly':
            return {
                label: 'Read Only',
                bgColor: 'bg-badge-readonly',
                borderColor: 'border-badge-readonly/40',
            };
    }
};

const getBonusValues = (variant: BadgeValuesBonusType): BadgeStatusValuesType => {
    switch (variant) {
        case 'active':
            return {
                label: 'Active',
                bgColor: 'bg-badge-active',
                borderColor: 'border-badge-active/40',
            };
        case 'canceled':
            return {
                label: 'Canceled',
                bgColor: 'bg-badge-cancelled',
                borderColor: 'border-badge-cancelled/40',
            };
        case 'expired':
            return {
                label: 'Expired',
                bgColor: 'bg-badge-expired',
                borderColor: 'border-badge-expired/40',
            };

        case 'claimed':
            return {
                label: 'Claimed',
                bgColor: 'bg-badge-claimed',
                borderColor: 'border-badge-claimed/40',
            };
        case 'rolled-back':
            return {
                label: 'Rolled back',
                bgColor: 'bg-badge-cancelled',
                borderColor: 'border-badge-cancelled/40',
            };
    }
};

export const BadgeStatus: FC<BadgeStatusType> = ({ variant, className, dataTest, ...props }): ReactElement => {
    const { label, borderColor, bgColor } = getBadgeStatusValues(variant);
    return (
        <div
            className={cn(
                'px-2.5 py-0.5 h-5 flex items-center justify-center gap-1 border border-solid rounded-md w-min cursor-default whitespace-nowrap',
                borderColor,
                className
            )}
            data-test={dataTest}
            {...props}
        >
            <span className={`block rounded-full size-1.5 ${bgColor}`} />
            <span className='text-xs'>{label}</span>
        </div>
    );
};

export const BadgeBonus: FC<BadgeBonusType> = ({ variant, className, dataTest, ...props }): ReactElement => {
    const { label, borderColor, bgColor } = getBonusValues(variant);
    return (
        <div
            className={cn(
                'px-2.5 py-0.5 h-5 flex items-center justify-center gap-1 border border-solid rounded-md w-min cursor-default whitespace-nowrap',
                borderColor,
                className
            )}
            data-test={dataTest}
            {...props}
        >
            <span className={`block rounded-full size-1.5 ${bgColor}`} />
            <span className='text-xs font-normal'>{label}</span>
        </div>
    );
};
